import React, { useState } from 'react';
import { useGlobal, Tooltip, globalizationPath, Link } from '@rcg/ui-react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { CardContent, Chip, Collapse, useMediaQuery } from '@material-ui/core';
import AirlineSeatReclineNormalIcon from '@material-ui/icons/AirlineSeatReclineNormal';
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff';
import FlightLandIcon from '@material-ui/icons/FlightLand';
import CloseIcon from '@material-ui/icons/Close';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import Divider from '@material-ui/core/Divider';
import { navigate } from '@gatsbyjs/reach-router';
import { Img } from 'react-image';
import { formatPrice } from '@rcg/pricing-format';
import Modal from '@material-ui/core/Modal';
import InfoIcon from '@material-ui/icons/Info';
import FareTooltip from '../../../FareTooltip';
import Button from '../../../UiComponents/Button';
import AirLoader from '../../../AirLoader';
import useDictionary from '../../../../hooks/useDictionary';
import { ReactComponent as DefaultIcon } from '../../../../assets/icons/icon-missing-carrier.svg';
import useStyles from './useStyles';
import Dictionary from '../../../../utils/dictionary';
import TimeDuration, { timeWithLocale } from '../../../../utils/timeFormats';
import layoverFormats from '../../../../utils/layoverFormats';
import entertainmentImage from '../../../../assets/icons/amenities/play.png';
import luggageCheck from '../../../../assets/icons/amenities/travel.png';
import martini from '../../../../assets/icons/amenities/beberage.png';
import aadvantage from '../../../../assets/icons/amenities/aadvantage.jpg';
import bagWithFee from '../../../../assets/icons/amenities/bag_with_fee.png';
import changeFlightIcon from '../../../../assets/icons/amenities/change_flight.png';
import businessClassIcon from '../../../../assets/icons/amenities/business_class_seat.png';
import informationIcon from '../../../../assets/icons/amenities/informational.png';
import cabinClassIcon from '../../../../assets/icons/amenities/main_cabin_plus.png';
import mainSeatIcon from '../../../../assets/icons/amenities/main_seat.png';
import powerIcon from '../../../../assets/icons/amenities/power.png';
import seatedTimeIcon from '../../../../assets/icons/amenities/aviation.png';
import wifiIcon from '../../../../assets/icons/amenities/wifi.png';
import prereservedSeatIcon from '../../../../assets/icons/amenities/prereserved-seat.jpg';
import bagsIcon from '../../../../assets/icons/amenities/portfolio.png';
import {
  FLIGHT_SEARCH_TYPE,
  NAVIGATION_PATHS,
  SESSION_STORAGE_NO_FLIGHTS_RESULTS_ERROR,
  FLIGHT_TYPE_CODES,
} from '../../../../utils/constants';
import { getGuests } from '../../../../utils/payment';
import {
  heldReservationVar,
  shouldSearchReturnResultsVar,
  searchResultsVar,
  bookingProfileVar,
  searchParamsVar,
} from '../../../../operations/localState/reactiveVariables';
import setSearchResults from '../../../../operations/localState/setSearchResults';
import setSelectedDeparture from '../../../../operations/localState/setSelectedDeparture';
import setSelectedReturn from '../../../../operations/localState/setSelectedReturn';
import setHeldReservation from '../../../../operations/localState/setHeldReservation';
import { FLIGHTS_HOLD_RESERVATION } from '../../../../operations/mutations';
import { FLIGHT_PROFILE_EMAIL } from '../../../../operations/queries';
import { setBookingEmail } from '../../../../operations/localState/setBookingProfile';
import baggage from '../../../../utils/baggage';
import { getBaggageIcon, getOverNightIcon } from '../../../../utils/icons';
import Theme from '../../../../utils/theme';
import { setSessionStorageItem } from '../../../../operations/sessionStorage/utils';

const BaggageTooltip = ({ cabin }) => {
  const classes = useStyles();
  const { renderContent } = useGlobal();
  const { baggageInfo, currency } = cabin;
  const baggages = baggage.buildBaggages(baggageInfo, renderContent, currency);

  const baggageContent = () => {
    return (
      <div className={classes.baggageTooltip}>
        <div className={classes.baggageTooltipTitle}>
          {renderContent(
            Dictionary.airBaggageTooltipTitle.key,
            Dictionary.airBaggageTooltipTitle.defaultValue,
          )}
        </div>
        {baggages.map((baggageData, key) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={key} className={classes.baggageTooltipBags}>
              <span>{baggageData.title}</span>
              <span>{baggageData.price}</span>
            </div>
          );
        })}
        {baggages.length === 0 && (
          <div className={classes.baggageTooltipBags}>
            <div>
              {renderContent(
                Dictionary.airBaggageTooltipNoBaggages.key,
                Dictionary.airBaggageTooltipNoBaggages.defaultValue,
              )}
            </div>
          </div>
        )}
        <div className={classes.baggageTooltipAdditionalInfo}>
          {renderContent(
            Dictionary.airBaggageTooltipAditionalInfo.key,
            Dictionary.airBaggageTooltipAditionalInfo.defaultValue,
          )}
        </div>
      </div>
    );
  };
  return (
    <span className={classes.tooltipLabel}>
      <Tooltip
        element={<InfoIcon fontSize="small" />}
        content={baggageContent()}
        tooltipProps={{
          PopperProps: {
            modifiers: {
              offset: {
                enabled: true,
                offset: `30, 0`,
              },
            },
          },
        }}
      />
    </span>
  );
};

const TakeOffTime = (props) => {
  const classes = useStyles();
  const {
    data: { localizedDayjs },
  } = useGlobal();
  const { flight, segment, segmentIndex } = props;
  const isSm = useMediaQuery(Theme.breakpoints.down('sm'));

  return (
    <>
      <Img
        src={segment.marketingAirline.icon}
        alt=""
        className={classes.multiAirlineLogo}
        unloader={<DefaultIcon className={classes.multiAirlineLogo} />}
      />

      <p>
        <FlightTakeoffIcon className={classes.expandedDepartIcon} />
        {timeWithLocale(localizedDayjs, segment.departure.time)}
        &nbsp;
        {segmentIndex === 0 && <>&nbsp;</>}
        {segmentIndex !== 0 && (
          <>
            <span className={classes.layoverDays}>
              {layoverFormats.layoverDays({
                departure: flight.flightSegments[segmentIndex - 1].arrival.date,
                arrival: segment.departure.date,
              })}
            </span>
            &nbsp;
          </>
        )}
        {!isSm && `- ${segment.departure.airport.name} (${segment.departure.airport.code})`}
      </p>
    </>
  );
};

const FlightExpanded = (props) => {
  const classes = useStyles();
  const getDictionary = useDictionary(Dictionary);
  const {
    data: { lang, market, localizedDayjs },
    renderContent,
  } = useGlobal();
  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const { flight, expanded, index, flightType } = props;
  const searchResults = searchResultsVar();
  const [lastSelectedCabinHashKey, setLastSelectedCabinHashKey] = useState(null);
  const [modalAmenities, setModalAmenities] = useState(false);
  const [amenitiesList, setAmenitiesList] = useState([]);

  const ctaLabel = (flightDetails, cabinIndex) => {
    return flightDetails.length > 1
      ? formatPrice(
          flightDetails[cabinIndex].pricePerPassenger.value,
          flightDetails[cabinIndex].pricePerPassenger.currency,
          true,
          true,
          true,
        )
      : getDictionary(Dictionary.airSelectFlightLabel.key);
  };

  const goToDepartureWithNoFlights = () => {
    setSearchResults(
      {
        flightFilters: null,
        matchingFlights: [],
        flightType: FLIGHT_SEARCH_TYPE.DEPARTURE,
      },
      true,
    );
    setSessionStorageItem(SESSION_STORAGE_NO_FLIGHTS_RESULTS_ERROR, true);
    navigate(`${urlPath}${NAVIGATION_PATHS.resultsDeparture}`);
  };

  const [getBookingEmail, { loading: flightProfileLoading }] = useLazyQuery(FLIGHT_PROFILE_EMAIL, {
    fetchPolicy: 'no-cache',
    variables: {
      limit: -1,
    },
    onCompleted: (data) => {
      setBookingEmail(data.flightProfile.guests[0].email);
      navigate(`${urlPath}${NAVIGATION_PATHS.reviewFlight}`);
    },
    onError: () => {
      navigate(`${urlPath}${NAVIGATION_PATHS.error}`);
    },
  });

  const [addReservation, { loading: reservationLoading }] = useMutation(FLIGHTS_HOLD_RESERVATION, {
    onCompleted: (data) => {
      setHeldReservation({
        ...data,
        associatedHashKey: lastSelectedCabinHashKey,
      });
      getBookingEmail();
    },
    onError: (error) => {
      if (
        String(error).includes('Re-hold is not allowed') ||
        String(error).includes('ACE-AB-1210')
      ) {
        if (
          heldReservationVar() &&
          heldReservationVar().associatedHashKey === lastSelectedCabinHashKey
        ) {
          navigate(`${urlPath}${NAVIGATION_PATHS.reviewFlight}`);
        } else {
          goToDepartureWithNoFlights();
        }
      } else if (
        String(error).includes('Flight not available') ||
        String(error).includes('ACE-AB-1209')
      ) {
        goToDepartureWithNoFlights();
      } else {
        navigate(`${urlPath}${NAVIGATION_PATHS.error}`);
      }
    },
  });
  const bookingProfile = bookingProfileVar();
  const selectCabin = (selectedFlight, selectedCabin) => {
    if (flightType === FLIGHT_SEARCH_TYPE.DEPARTURE) {
      setSelectedDeparture({
        flight: selectedFlight,
        cabin: selectedCabin,
      });
      if (bookingProfile?.bookingToken) {
        if (flight?.routeType?.code === FLIGHT_TYPE_CODES.ONE_WAY) {
          setLastSelectedCabinHashKey(selectedCabin.hashKey);
          const guests = getGuests(bookingProfileVar, searchParamsVar);
          addReservation({
            variables: {
              correlationID: searchResults.correlationID,
              hashKey: selectedCabin.hashKey,
              bookingID: bookingProfile?.currentBooking?.id,
              guests,
            },
          });
        } else {
          navigate(`${urlPath}${NAVIGATION_PATHS.resultsReturn}`);
          shouldSearchReturnResultsVar(true);
        }
      } else if (
        !bookingProfile?.bookingToken &&
        flight?.routeType?.code === FLIGHT_TYPE_CODES.ONE_WAY
      ) {
        navigate(`${urlPath}${NAVIGATION_PATHS.browseSummary}`);
      } else {
        navigate(`${urlPath}${NAVIGATION_PATHS.browseReturn}`);
      }
    } else {
      setSelectedReturn({
        flight: selectedFlight,
        cabin: selectedCabin,
      });
      setLastSelectedCabinHashKey(selectedCabin.hashKey);
      if (bookingProfile && bookingProfile.bookingToken) {
        const guests = getGuests(bookingProfileVar, searchParamsVar);
        addReservation({
          variables: {
            correlationID: searchResults.correlationID,
            hashKey: selectedCabin.hashKey,
            bookingID: bookingProfile?.currentBooking?.id,
            guests,
          },
        });
      } else {
        navigate(`${urlPath}${NAVIGATION_PATHS.browseSummary}`);
      }
    }
  };

  const openModalAmenities = (amenities) => {
    const translateAmenities = amenities.map((amenitie) => {
      return {
        ...amenitie,
        value: renderContent(`air${amenitie.key}`, amenitie.value),
      };
    });
    setAmenitiesList(translateAmenities);
    setModalAmenities(true);
  };

  const closeModalAmenities = () => {
    setModalAmenities(false);
    setAmenitiesList(null);
  };

  const getAmenityIcon = (code, label) => {
    let amenityIcon = null;
    switch (code) {
      case 'CH2BFF':
        amenityIcon = <img src={bagsIcon} alt="" height={20} />;
        break;
      case 'MCA1FB':
      case 'PRBAG':
        amenityIcon = <img src={luggageCheck} alt="" height={20} />;
        break;
      case 'MLALCD':
      case 'MLBV':
        amenityIcon = <img src={martini} alt="" height={20} />;
        break;
      case 'ENTDP':
      case 'ENTFP':
      case 'ENTHS':
      case 'ENTINFLT':
      case 'ENTLVSAT':
      case 'ENTOVH':
      case 'ENTPE':
      case 'ENTVG':
        amenityIcon = <img src={entertainmentImage} alt="" height={20} />;
        break;
      case 'EL4AAM':
        amenityIcon = <img src={aadvantage} alt="" height={20} />;
        break;
      case 'PYTCHB':
        amenityIcon = <img src={bagWithFee} alt="" height={20} />;
        break;
      case 'FRTOMP':
      case 'PYTCSF':
      case 'RFTRTC':
      case 'RFTTCR':
        amenityIcon = <img src={changeFlightIcon} alt="" height={20} />;
        break;
      case 'COUMLC':
        amenityIcon = <img src={businessClassIcon} alt="" height={20} />;
        break;
      case 'CHALWD':
      case 'CHSDFL':
      case 'CSDCIF':
      case 'EL4UOA':
      case 'NCFDTP':
        amenityIcon = <img src={informationIcon} alt="" height={20} />;
        break;
      case 'CAEMLP':
      case 'CAESML':
        amenityIcon = <img src={cabinClassIcon} alt="" height={20} />;
        break;
      case 'CHYSAN':
      case 'CHYSMA':
        amenityIcon = <img src={mainSeatIcon} alt="" height={20} />;
        break;
      case 'PWRIS':
      case 'USB':
        amenityIcon = <img src={powerIcon} alt="" height={20} />;
        break;
      case 'PRSDS':
      case 'PRPRSA':
      case 'LGAC':
      case 'LGADMC':
        amenityIcon = <img src={prereservedSeatIcon} alt="" height={20} />;
        break;
      case 'COPIAO':
      case 'GNRLBD':
      case 'PG5BDN':
      case 'PRBONB':
      case 'PRBRD':
      case 'PRCHKIN':
      case 'PRCSGB':
      case 'PRCSIG':
      case 'PRFT':
      case 'PRPS':
        amenityIcon = <img src={luggageCheck} alt="" height={20} />;
        break;
      case 'SAPRM':
      case 'SALFS':
        amenityIcon = <img src={seatedTimeIcon} alt="" height={20} />;
        break;
      case 'ENTPW':
      case 'WIFI':
        amenityIcon = <img src={wifiIcon} alt="" height={20} />;
        break;
      case 'ENTPABP':
      case 'ENTTAK':
      case 'MLBRKF':
      case 'MLBRKFE':
      case 'MLCBRKF':
      case 'MLHD':
      case 'MLLUN':
      case 'MLNALD':
      case 'MLSNCK':
      case 'PRFC':
      case 'PRSDFC':
      case 'SA2FB':
        amenityIcon = null;
        break;
      default:
        amenityIcon = null;
        break;
    }

    if (amenityIcon === null) return null;

    return (
      <div className={classes.amenitiesItemList}>
        <div>{amenityIcon}</div>
        <div>{label}</div>
      </div>
    );
  };

  const getListIcons = (allServices) => {
    if (allServices?.length < 1) return null;
    const labelWithIcons = allServices?.map((amenity) => {
      return getAmenityIcon(amenity.key, amenity.value);
    });
    const filteresLabelWithIcons = labelWithIcons?.filter((item) => item !== null);
    if (!filteresLabelWithIcons) return null;
    return labelWithIcons;
  };

  return (
    <>
      <AirLoader
        open={reservationLoading || flightProfileLoading}
        status={
          (reservationLoading || flightProfileLoading) &&
          getDictionary(Dictionary.loaderHoldingFlights.key)
        }
      />
      <Collapse
        className={classes.expandedCard}
        in={expanded[index]}
        timeout={{ enter: 500, exit: 1 }}>
        <CardContent classes={{ root: classes.expandedRoot }}>
          {flight.flightSegments.map((segment, segmentIndex) => (
            // eslint-disable-next-line
            <div className={classes.expandedItinerary} key={segmentIndex}>
              <div className={classes.expandedAirportInfo}>
                <TakeOffTime flight={flight} segment={segment} segmentIndex={segmentIndex} />
              </div>
              <div className={classes.expandedAirportInfoSmall}>
                <TakeOffTime flight={flight} segment={segment} segmentIndex={segmentIndex} />
                <p className={classes.airportLabelSmall}>
                  {`${segment.departure.airport.city} (${segment.departure.airport.code})`}
                </p>
              </div>
              <div className={classes.expandedFlightDuration}>
                <div className={classes.expandedDurationIcons}>
                  <MoreVertIcon className={classes.upperVertical} />
                  <MoreVertIcon className={classes.lowerVertical} />
                </div>
                <div className={classes.expandedDuration}>
                  <span className={classes.expandedDurationLabel}>
                    {TimeDuration(segment.durationMins)}
                  </span>
                  {layoverFormats.isOvernightFlight({
                    departure: segment.departure.date,
                    arrival: segment.arrival.date,
                  }) && (
                    <Chip
                      className={classes.overnightLayover}
                      avatar={getOverNightIcon()}
                      label={getDictionary(Dictionary.airOvernightFlightLabel.key)}
                      variant="outlined"
                    />
                  )}
                </div>
              </div>
              <div className={classes.expandedAirportInfo}>
                <p>
                  <FlightLandIcon className={classes.expandedDepartIcon} />
                  {timeWithLocale(localizedDayjs, segment.arrival.time)}
                  &nbsp;
                  <span className={classes.layoverDays}>
                    {layoverFormats.layoverDays({
                      arrival: segment.arrival.date,
                      departure: flight.departDate,
                    })}
                  </span>
                  &nbsp;
                  {`- ${segment.arrival.airport.name} (${segment.arrival.airport.code})`}
                </p>
              </div>
              <div className={classes.expandedAirportInfoSmall}>
                <p>
                  <FlightLandIcon className={classes.expandedDepartIcon} />
                  {timeWithLocale(localizedDayjs, segment.arrival.time)}
                  <span className={classes.layoverDays}>
                    &nbsp;
                    {layoverFormats.layoverDays({
                      arrival: segment.arrival.date,
                      departure: flight.departDate,
                    })}
                  </span>
                </p>
                <p className={classes.airportLabelSmall}>
                  {`${segment.arrival.airport.city} (${segment.arrival.airport.code})`}
                </p>
              </div>
              <div className={classes.expandedFlightInfo}>
                <p>
                  {`${getDictionary(Dictionary.airOperatorLabel.key)} `}
                  {segment.operatingAirline.name}&nbsp;&nbsp;|&nbsp;&nbsp;
                  {segment?.airplane?.name}&nbsp;&nbsp;|&nbsp;&nbsp;
                  {`${segment.marketingAirline.code} ${segment.number}`}
                </p>
              </div>
              {segment.layoverMins > 0 && (
                <div className={classes.expandedLayover}>
                  <Divider classes={{ root: classes.expandedSeparator }} />
                  <div className={classes.expandedLayoverDuration}>
                    <span>
                      {TimeDuration(segment.layoverMins)}{' '}
                      {getDictionary(Dictionary.airLayoverLabel.key)}
                    </span>
                    &nbsp;•&nbsp;
                    {`${segment.arrival.airport.city} (${segment.arrival.airport.code})`}
                    {flight.flightSegments[segmentIndex + 1] &&
                      layoverFormats.isOvernightLayover({
                        departure: flight.flightSegments[segmentIndex + 1].departure.date,
                        arrival: segment.arrival.date,
                      }) && (
                        <Chip
                          className={classes.overnightLayover}
                          avatar={getOverNightIcon()}
                          label={getDictionary(Dictionary.airOvernightLayoverLabel.key)}
                          variant="outlined"
                        />
                      )}
                  </div>
                  <Divider classes={{ root: classes.expandedSeparator }} />
                </div>
              )}
            </div>
          ))}
          <Divider classes={{ root: classes.expandedSeparator }} />
          <div className={classes.expandedCabins}>
            <div className={classes.expandedChooseCabinTitle}>
              <p>
                <AirlineSeatReclineNormalIcon className={classes.expandedChooseCabinIcon} />
                {getDictionary(Dictionary.airChooseCabinTitle.key)}
              </p>
            </div>
            <div className={classes.expandedCabinsCard}>
              {flight.details.map((cabin, cabinIndex) => (
                // eslint-disable-next-line
                <div className={classes.cabinCard} key={cabinIndex}>
                  <div
                    className={`${classes.cabinClassColorCode} ${
                      cabin.cabinClass.code === 'Y' ? classes.economy : ''
                    }${cabin.cabinClass.code === 'S' ? classes.premiumEconomy : ''}${
                      cabin.cabinClass.code === 'C' ? classes.business : ''
                    }${cabin.cabinClass.code === 'F' ? classes.firstClass : ''}`}
                  />
                  <div className={classes.contentContianer}>
                    <div className={classes.content}>
                      <div className={classes.cabinTypeInfo}>
                        <div className={classes.cabinType}>
                          <h3>
                            {renderContent(
                              Dictionary[`airClass${cabin.cabinClass.code}Name`].key,
                              Dictionary[`airClass${cabin.cabinClass.code}Name`].defaultValue,
                            )}
                          </h3>
                          <span className={classes.cabinPaymentOptions}>
                            {cabin.isPaymentRequired
                              ? getDictionary(Dictionary.airPaymentRequiredLabel.key)
                              : getDictionary(Dictionary.airPaymentNotRequiredLabel.key)}
                            &nbsp;&nbsp;|&nbsp;&nbsp;
                            <FareTooltip
                              elementLabel={
                                cabin.isRefundable
                                  ? getDictionary(Dictionary.airRefundableLabel.key)
                                  : getDictionary(Dictionary.airNotRefundableLabel.key)
                              }
                            />
                          </span>
                          <div className={classes.cabinPaymentOptionsSmall}>
                            <p>
                              {cabin.isPaymentRequired
                                ? getDictionary(Dictionary.airPaymentRequiredLabel.key)
                                : getDictionary(Dictionary.airPaymentNotRequiredLabel.key)}
                            </p>
                            <FareTooltip
                              elementLabel={
                                cabin.isRefundable
                                  ? getDictionary(Dictionary.airRefundableLabel.key)
                                  : getDictionary(Dictionary.airNotRefundableLabel.key)
                              }
                            />
                          </div>
                        </div>
                        <div className={classes.cabinBaggage}>
                          <p className={classes.cabinBaggageLabel}>
                            {getBaggageIcon()} {cabin.baggageInfo.freeBaggageCount}
                          </p>
                          {/* Implement tooltip */}
                          <div className={classes.cabinBaggageTooltip}>
                            <p>
                              {renderContent(
                                Dictionary.airBaggageLabel.key,
                                Dictionary.airBaggageLabel.defaultValue,
                              )}
                            </p>
                            <BaggageTooltip cabin={cabin} />
                            <span className={classes.cabinSeatsLeftSmall}>
                              {cabin.seatsAvailable <= 8 && (
                                <span className={classes.seatsLeft}>
                                  {cabin.seatsAvailable}
                                  {cabin.seatsAvailable === 1
                                    ? getDictionary(Dictionary.airSeatLeftLabel.key)
                                    : getDictionary(Dictionary.airSeatsLeftLabel.key)}
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className={classes.cabinPriceInfo}>
                        <Button
                          data-testid={`select-cabin-btn-${index}-${cabinIndex}`}
                          onClick={() => {
                            selectCabin(flight, cabin);
                          }}
                          variant="contained"
                          color="primary">
                          {ctaLabel(flight.details, cabinIndex)}
                        </Button>
                        <p className={classes.cabinSeatsLeft}>
                          {cabin.seatsAvailable <= 8 && (
                            <span className={classes.seatsLeft}>
                              {cabin.seatsAvailable}
                              {cabin.seatsAvailable === 1
                                ? getDictionary(Dictionary.airSeatLeftLabel.key)
                                : getDictionary(Dictionary.airSeatsLeftLabel.key)}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {getListIcons(cabin.inflightServices) && (
                      <div className={classes.amenitiesLink}>
                        {
                          // eslint-disable-next-line jsx-a11y/anchor-is-valid
                          <Link
                            data-testid={`open-modal-amenities-${cabinIndex}`}
                            component="button"
                            underline="none"
                            onClick={() => openModalAmenities(cabin.inflightServices)}>
                            <span>
                              {getDictionary(Dictionary.airButtonModalWhatsIncluding.key)}
                            </span>
                          </Link>
                        }
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Modal
            open={modalAmenities}
            onClose={closeModalAmenities}
            aria-labelledby="Extend Session"
            aria-describedby="Extend Session"
            disableBackdropClick
            disableEscapeKeyDown
            className={classes.test}>
            <div className={classes.amenitiesDetailsBody}>
              <div className={classes.modalAmenitiesTitle}>
                <span className={classes.modalAmenitiesSpanTitle}>
                  {getDictionary(Dictionary.airButtonModalWhatsIncluding.key)}
                </span>
                <CloseIcon
                  data-testid="close-modal-amenities"
                  className={classes.flightDetailsClose}
                  onClick={closeModalAmenities}
                />
              </div>
              <div className={classes.expandedItineraryModal}>{getListIcons(amenitiesList)}</div>
            </div>
          </Modal>
        </CardContent>
      </Collapse>
    </>
  );
};

export default FlightExpanded;
